// src/pages/MerchantStore.tsx
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  CardMedia,
  Chip,
  Stack,
  Paper,
  Avatar,
  Tabs,
  Tab,
  useTheme,
  alpha,
  Divider,
  Breadcrumbs,
  Link,
  CircularProgress,
  Alert,
  Snackbar,
  Drawer,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  TextField,
  FormControl
} from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getBestImageUrl, getPublicImageUrl } from '../services/s3Service';
import AppointmentBookingDialog from '../components/AppointmentBookingDialog';
import { InternalToolsProductService, Organization, SoldProduct, Order, CartItem } from '../types/marketTypes';

const API_BASE_URL = 'https://internaltoolsapi-ahdj.onrender.com/api';

// Simple emoji icons
const ShoppingCartIcon = () => <Box component="span" sx={{ fontSize: '20px', display: 'flex', alignItems: 'center' }}>🛒</Box>;
const CloseIcon = () => <Box component="span" sx={{ fontSize: '20px', display: 'flex', alignItems: 'center' }}>✕</Box>;
const AddIcon = () => <Box component="span" sx={{ fontSize: '20px', display: 'flex', alignItems: 'center' }}>+</Box>;
const RemoveIcon = () => <Box component="span" sx={{ fontSize: '20px', display: 'flex', alignItems: 'center' }}>−</Box>;
const DeleteIcon = () => <Box component="span" sx={{ fontSize: '20px', display: 'flex', alignItems: 'center' }}>🗑️</Box>;

const MerchantStore: React.FC = () => {
  const theme = useTheme();
  const { orgId } = useParams<{ orgId: string }>();
  
  // Data states
  const [merchant, setMerchant] = useState<Organization | null>(null);
  const [products, setProducts] = useState<InternalToolsProductService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState(0);
  
  // Image URLs state - EXACT SAME AS SHOP
  const [productImageUrls, setProductImageUrls] = useState<{[key: string]: string}>({});
  const [merchantLogoUrl, setMerchantLogoUrl] = useState<string>('');
  
  // Cart states (EXACT SAME AS SHOP)
  const [cart, setCart] = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [orderPlacing, setOrderPlacing] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>('Cash on Delivery');
  const [deliveryAddress, setDeliveryAddress] = useState('');
  
  // Appointment states
  const [appointmentDialogOpen, setAppointmentDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState<InternalToolsProductService | null>(null);
  
  // Notification states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });

  useEffect(() => {
    if (orgId) {
      fetchMerchantData();
    }
  }, [orgId]);

  // Load images after products are fetched - EXACT SAME AS SHOP
  useEffect(() => {
    const loadImages = async () => {
      if (products.length > 0) {
        const urls: {[key: string]: string} = {};
        
        // Load images for each product - EXACT SAME AS SHOP LOGIC
        await Promise.all(
          products.map(async (product) => {
            const url = await getProductImageUrl(product);
            urls[product.uniqueProductId] = url;
          })
        );
        
        setProductImageUrls(urls);
      }
      
      // Load merchant logo
      if (merchant?.orgLogo) {
        try {
          const logoUrl = await getBestImageUrl(merchant.orgLogo);
          setMerchantLogoUrl(logoUrl);
        } catch (error) {
          console.error('Failed to load merchant logo:', merchant.officialOrgName, error);
          setMerchantLogoUrl(getFallbackLogo(merchant.officialOrgName));
        }
      } else if (merchant) {
        setMerchantLogoUrl(getFallbackLogo(merchant.officialOrgName));
      }
    };
    
    if (!loading && (products.length > 0 || merchant)) {
      loadImages();
    }
  }, [loading, products, merchant]);

  const fetchMerchantData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch organizations and products
      const [organizationsRes, productsRes] = await Promise.all([
        fetch(`${API_BASE_URL}/Providers/GetAllOrganizations`),
        fetch(`${API_BASE_URL}/ProductsServices/GetOrgOnlineProductsServices/${orgId}`)
      ]);

      if (!organizationsRes.ok || !productsRes.ok) {
        throw new Error('Failed to fetch merchant data');
      }

      const allOrganizations: Organization[] = await organizationsRes.json();
      const allProducts: InternalToolsProductService[] = await productsRes.json();

      // Find specific merchant
      const foundMerchant = allOrganizations.find(org => org.orgUniqueId === orgId);
      
      if (!foundMerchant) {
        throw new Error('Merchant not found');
      }

      // Filter products for this merchant (only online listed and available)
      const merchantProducts = allProducts.filter(product => 
        product.orgUniqueId === orgId && 
        product.onlineListedFlag === true && 
        product.productAvailabilityFlag === true
      );

      setMerchant(foundMerchant);
      setProducts(merchantProducts);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load merchant data');
      console.error('Error fetching merchant data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Get product image URL - EXACT SAME AS SHOP
  const getProductImageUrl = async (product: InternalToolsProductService): Promise<string> => {
    // Try productPictures array first
    if (product.productPictures && product.productPictures.length > 0) {
      const imageKey = product.productPictures[0];
      if (imageKey) {
        try {
          // Use the S3 service to get the best available URL
          const url = await getBestImageUrl(imageKey);
          return url;
        } catch (error) {
          console.error('Failed to get S3 URL for product:', product.productName, error);
        }
      }
    }
    
    // Default fallback - use emoji placeholder
    return getFallbackProductImage(product.productType);
  };

  // Better fallback images
  const getFallbackProductImage = (productType: string): string => {
    // Return emoji based on product type
    switch(productType) {
      case 'Service': return '🔧';
      case 'Pharmaceutical': return '💊';
      case 'Input': return '⚙️';
      default: return '📦';
    }
  };

  const getFallbackLogo = (name: string): string => {
    return '🏪'; // Store emoji
  };

  // Filter products by type
  const getProductsByType = (type: string) => {
    return products.filter(product => product.productType === type);
  };

  // CART FUNCTIONS - EXACT SAME AS SHOP
  const addToCart = (product: InternalToolsProductService) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(item => item.productId === product._id);
      if (existingItem) {
        return prevCart.map(item =>
          item.productId === product._id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        return [...prevCart, {
          productId: product._id,
          uniqueProductId: product.uniqueProductId,
          productName: product.productName,
          productPriceInUSD: product.productPriceInUSD,
          quantity: 1,
          productImage: productImageUrls[product._id] || getFallbackProductImage(product.productType),
          quantityMetric: product.quantityMetric,
          productType: product.productType,
          productDescription: product.productDescription
        }];
      }
    });
    
    showNotification('Product added to cart', 'success');
  };

  const removeFromCart = (productId: string) => {
    setCart(prevCart => prevCart.filter(item => item.productId !== productId));
    showNotification('Product removed from cart', 'info');
  };

  const updateQuantity = (productId: string, newQuantity: number) => {
    if (newQuantity < 1) {
      removeFromCart(productId);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        item.productId === productId
          ? { ...item, quantity: newQuantity }
          : item
      )
    );
  };

  const cartTotal = cart.reduce((total, item) => total + (item.productPriceInUSD * item.quantity), 0);
  const cartItemsCount = cart.reduce((count, item) => count + item.quantity, 0);

  // Generate order ID
  const generateOrderId = (): string => {
    const timestamp = new Date().getTime();
    const random = Math.floor(Math.random() * 10000);
    return `ORD-${timestamp}-${random}`;
  };

  // Place order - EXACT SAME AS SHOP
  const placeOrder = async () => {
    if (cart.length === 0) {
      showNotification('Cart is empty', 'error');
      return;
    }

    if (!deliveryAddress.trim()) {
      showNotification('Please enter delivery address', 'error');
      return;
    }

    setOrderPlacing(true);
    setOrderError(null);

    try {
      const cartList: SoldProduct[] = cart.map(item => {
        // Find the original product
        const originalProduct = products.find(p => p._id === item.productId);
        
        return {
          wellnessProductId: '',
          uniqueProductId: item.uniqueProductId,
          wellnessProductTag: originalProduct?.productTag || 'marketplace',
          productName: item.productName,
          provider: merchant?.officialOrgName || 'Merchant',
          productPrice: item.productPriceInUSD,
          productDescription: item.productDescription,
          officialTimeTakenInMins: 0,
          variation: 'standard',
          wellnessPillar: '',
          productType: item.productType,
          payers: [],
          productQuantityBought: item.quantity,
          quantityMetric: item.quantityMetric,
          valueAddedTaxOrEquivalentPercentage: originalProduct?.valueAddedTaxOrEquivalentPercentage || 0.15
        };
      });

      const order: Order = {
        orderId: generateOrderId(),
        orgUniqueId: orgId || '',
        orderStatus: 'pending',
        customerUserName: 'guest-customer',
        providerUserName: merchant?.officialOrgName || 'Merchant',
        orderDateTimeStamp: new Date().toISOString(),
        paymentStatus: 'pending',
        paymentMethodPreferred: paymentMethod,
        saleUniqueId: '',
        orderPriority: 'normal',
        deliveryAddress: deliveryAddress,
        deliveryDateTimeStamp: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        deliveryStatus: 'scheduled',
        cartList: cartList
      };

      const response = await fetch(`${API_BASE_URL}/Services/PlaceOrder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(order)
      });

      if (!response.ok) {
        throw new Error(`Failed to place order: ${response.statusText}`);
      }

      const placedOrder = await response.json();
      
      setCart([]);
      setOrderSuccess(true);
      setCheckoutOpen(false);
      showNotification('Order placed successfully!', 'success');
      
      setTimeout(() => {
        setOrderSuccess(false);
      }, 3000);

    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to place order';
      setOrderError(errorMsg);
      showNotification(`Failed to place order: ${errorMsg}`, 'error');
      console.error('Error placing order:', err);
    } finally {
      setOrderPlacing(false);
    }
  };

  // Appointment booking handler
  const handleBookAppointment = (service: InternalToolsProductService) => {
    setSelectedService(service);
    setAppointmentDialogOpen(true);
  };

  const showNotification = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Payment methods
  const paymentMethods = [
    { id: 'Cash on Delivery', label: 'Cash on Delivery', enabled: true },
    { id: 'PayPal', label: 'PayPal', enabled: false },
    { id: 'Ecocash', label: 'Ecocash', enabled: false },
    { id: 'PayNow', label: 'PayNow', enabled: false },
    { id: 'Credit Card', label: 'Credit Card', enabled: false }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header cartItemsCount={cartItemsCount} />
        <Container sx={{ py: 10, textAlign: 'center', flexGrow: 1 }}>
          <CircularProgress />
          <Typography sx={{ mt: 2 }}>Loading store...</Typography>
        </Container>
        <Footer />
      </Box>
    );
  }

  if (error || !merchant) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <Header />
        <Container sx={{ py: 10, textAlign: 'center', flexGrow: 1 }}>
          <Typography variant="h4">{error || 'Store not found'}</Typography>
          <Button href="#/market" sx={{ mt: 2 }}>
            Back to Marketplace
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: '100vh',
      bgcolor: 'background.default'
    }}>
      {/* Header with cart badge */}
      <Header cartItemsCount={cartItemsCount} />
      
      {/* Main Content */}
      <Box component="main" sx={{ flexGrow: 1, py: 4 }}>
        <Container maxWidth="lg">
          {/* Breadcrumbs */}
          <Breadcrumbs sx={{ mb: 4 }}>
            <Link href="#/home" color="inherit">
              Home
            </Link>
            <Link href="#/market" color="inherit">
              Marketplace
            </Link>
            <Typography color="text.primary">{merchant.officialOrgName}</Typography>
          </Breadcrumbs>

          {/* Store Header */}
          <Paper
            elevation={0}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              mb: 4,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            {/* Hero Image */}
            <Box sx={{ 
              height: 200,
              bgcolor: alpha(theme.palette.secondary.main, 0.1),
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Box sx={{ 
                position: 'absolute',
                width: '100%',
                height: '100%',
                background: `linear-gradient(45deg, ${alpha(theme.palette.secondary.main, 0.2)}, ${alpha(theme.palette.primary.main, 0.2)})`
              }} />
              <Typography variant="h1" sx={{ 
                fontSize: '3rem',
                fontWeight: 900,
                color: 'white',
                textShadow: '0 2px 10px rgba(0,0,0,0.3)',
                position: 'relative',
                zIndex: 1
              }}>
                {merchant.officialOrgName}
              </Typography>
            </Box>

            {/* Store Info */}
            <Box sx={{ p: 4 }}>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap',
                gap: 4,
                alignItems: 'flex-start'
              }}>
                {/* Logo and Basic Info */}
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flex: 1 }}>
                  <Avatar
                    src={merchantLogoUrl}
                    sx={{ 
                      width: 120, 
                      height: 120,
                      border: `4px solid ${alpha(theme.palette.secondary.main, 0.2)}`,
                      fontSize: '48px'
                    }}
                  >
                    {merchant.officialOrgName.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                      {merchant.officialOrgName}
                    </Typography>
                    <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
                      {merchant.organizationType}
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 1 }}>
                      {merchant.organizationMerchantType} • {merchant.merchantClass}
                    </Typography>
                  </Box>
                </Box>

                {/* Contact Info & Cart Button */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    minWidth: 300
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Store Information
                  </Typography>
                  <Stack spacing={1.5} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box component="span">📍</Box>
                      <Typography variant="body2">
                        {merchant.primaryAddress || 'Location not specified'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box component="span">🏷️</Box>
                      <Typography variant="body2">
                        {merchant.organizationMerchantType}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box component="span">📦</Box>
                      <Typography variant="body2">
                        {products.length} products available
                      </Typography>
                    </Box>
                  </Stack>
                  
                  {/* View Cart Button */}
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<ShoppingCartIcon />}
                    onClick={() => setCartOpen(true)}
                    disabled={cart.length === 0}
                    sx={{
                      fontWeight: 600,
                      bgcolor: theme.palette.secondary.main,
                      '&:hover': {
                        bgcolor: theme.palette.secondary.dark
                      }
                    }}
                  >
                    View Cart ({cartItemsCount})
                  </Button>
                </Paper>
              </Box>
            </Box>
          </Paper>

          {/* Tabs */}
          <Paper elevation={0} sx={{ mb: 4 }}>
            <Tabs 
              value={activeTab} 
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label={`Products (${getProductsByType('Product').length + getProductsByType('Pharmaceutical').length + getProductsByType('Input').length})`} />
              <Tab label={`Services (${getProductsByType('Service').length})`} />
              <Tab label="Reviews" />
            </Tabs>
          </Paper>

          {/* Products/Services Grid */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap',
            margin: -2 
          }}>
            {(activeTab === 0 ? 
              [...getProductsByType('Product'), ...getProductsByType('Pharmaceutical'), ...getProductsByType('Input')] :
              activeTab === 1 ? getProductsByType('Service') : []
            ).map((product, index) => (
              <Box key={`${product._id}-${index}`} sx={{ 
                width: { xs: '100%', sm: '50%', md: '33.333%' }, 
                padding: 2 
              }}>
                <Card
                  sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'all 0.3s ease',
                    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[8],
                      borderColor: theme.palette.secondary.light
                    }
                  }}
                >
                  {/* Product Image - EXACT SAME AS SHOP */}
                  <Box sx={{ 
                    position: 'relative', 
                    pt: '75%',
                    bgcolor: alpha(theme.palette.grey[300], 0.2),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {productImageUrls[product.uniqueProductId] ? (
                      <CardMedia
                        component="img"
                        image={productImageUrls[product.uniqueProductId]}
                        alt={product.productName}
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          console.error('Failed to load image:', target.src);
                          // Show emoji placeholder on error
                          target.style.display = 'none';
                          const parent = target.parentElement;
                          if (parent) {
                            const emojiSpan = document.createElement('span');
                            emojiSpan.textContent = getFallbackProductImage(product.productType);
                            emojiSpan.style.fontSize = '48px';
                            emojiSpan.style.position = 'absolute';
                            emojiSpan.style.top = '50%';
                            emojiSpan.style.left = '50%';
                            emojiSpan.style.transform = 'translate(-50%, -50%)';
                            parent.appendChild(emojiSpan);
                          }
                        }}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <Box sx={{ 
                        fontSize: '48px',
                        color: theme.palette.text.secondary
                      }}>
                        {getFallbackProductImage(product.productType)}
                      </Box>
                    )}
                    {product.quantityAvailable < 10 && (
                      <Chip
                        label="Low Stock"
                        size="small"
                        color="warning"
                        sx={{
                          position: 'absolute',
                          top: 12,
                          right: 12,
                          fontWeight: 600
                        }}
                      />
                    )}
                  </Box>

                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 700, 
                      mb: 1,
                      height: '3rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {product.productName}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ 
                      mb: 2,
                      height: '3rem',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}>
                      {product.productDescription}
                    </Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: theme.palette.secondary.main }}>
                        ${product.productPriceInUSD.toFixed(2)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Stock: {product.quantityAvailable} {product.quantityMetric}
                      </Typography>
                    </Box>
                  </CardContent>

                  <CardActions sx={{ p: 3, pt: 0 }}>
                    {product.productType === 'Service' ? (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => handleBookAppointment(product)}
                        disabled={product.quantityAvailable === 0}
                        sx={{
                          fontWeight: 600,
                          py: 1.2,
                          bgcolor: theme.palette.info.main,
                          '&:hover': {
                            bgcolor: theme.palette.info.dark
                          }
                        }}
                      >
                        Book Appointment
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="contained"
                        onClick={() => addToCart(product)}
                        disabled={product.quantityAvailable === 0}
                        sx={{
                          fontWeight: 600,
                          py: 1.2,
                          bgcolor: theme.palette.secondary.main,
                          '&:hover': {
                            bgcolor: theme.palette.secondary.dark
                          }
                        }}
                      >
                        {product.quantityAvailable === 0 ? 'Out of Stock' : 'Add to Cart'}
                      </Button>
                    )}
                  </CardActions>
                </Card>
              </Box>
            ))}
          </Box>

          {/* Store Features */}
          <Paper
            elevation={0}
            sx={{
              mt: 8,
              p: 4,
              borderRadius: 4,
              bgcolor: alpha(theme.palette.primary.main, 0.03),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, textAlign: 'center' }}>
              Powered by Zendi POS
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              flexWrap: 'wrap',
              gap: 4,
              justifyContent: 'center'
            }}>
              {[
                { icon: '⚡', title: 'Real-time Inventory', desc: 'Stock updates instantly' },
                { icon: '💳', title: 'Secure Payments', desc: 'Multiple payment options' },
                { icon: '📱', title: 'Mobile Ready', desc: 'Shop from any device' },
                { icon: '🚚', title: 'Delivery Tracking', desc: 'Real-time order tracking' }
              ].map((feature, idx) => (
                <Box key={idx} sx={{ textAlign: 'center', width: 150 }}>
                  <Box sx={{ 
                    fontSize: '2.5rem',
                    mb: 1
                  }}>
                    {feature.icon}
                  </Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.desc}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Paper>
        </Container>
      </Box>

      {/* Footer */}
      <Footer />
      
      {/* Shopping Cart Drawer - EXACT SAME AS SHOP */}
      <Drawer
        anchor="right"
        open={cartOpen}
        onClose={() => setCartOpen(false)}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 400 } }
        }}
      >
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              Shopping Cart
            </Typography>
            <IconButton onClick={() => setCartOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>

          {cart.length === 0 ? (
            <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <Box sx={{ fontSize: 80, color: 'text.secondary', mb: 2, opacity: 0.5 }}>🛒</Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Your cart is empty
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Add some products to get started
              </Typography>
              <Button
                variant="contained"
                onClick={() => setCartOpen(false)}
              >
                Continue Shopping
              </Button>
            </Box>
          ) : (
            <>
              <List sx={{ flexGrow: 1, overflow: 'auto' }}>
                {cart.map((item) => (
                  <React.Fragment key={item.productId}>
                    <ListItem sx={{ px: 0 }}>
                      <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
                        <Box sx={{ 
                          width: 80, 
                          height: 80, 
                          borderRadius: 2,
                          overflow: 'hidden',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          bgcolor: alpha(theme.palette.grey[300], 0.2)
                        }}>
                          {item.productImage && item.productImage.startsWith('http') ? (
                            <img 
                              src={productImageUrls[item.uniqueProductId] || item.productImage} 
                              alt={item.productName}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                                const parent = target.parentElement;
                                if (parent) {
                                  const emojiSpan = document.createElement('span');
                                  emojiSpan.textContent = getFallbackProductImage(item.productType);
                                  emojiSpan.style.fontSize = '32px';
                                  parent.appendChild(emojiSpan);
                                }
                              }}
                            />
                          ) : (
                            <Box sx={{ fontSize: '32px' }}>
                              {item.productImage || getFallbackProductImage(item.productType)}
                            </Box>
                          )}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                            {item.productName}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            ${item.productPriceInUSD.toFixed(2)} each
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <IconButton 
                                size="small" 
                                onClick={() => updateQuantity(item.productId, item.quantity - 1)}
                              >
                                <RemoveIcon />
                              </IconButton>
                              <Typography sx={{ minWidth: 30, textAlign: 'center' }}>
                                {item.quantity}
                              </Typography>
                              <IconButton 
                                size="small" 
                                onClick={() => updateQuantity(item.productId, item.quantity + 1)}
                              >
                                <AddIcon />
                              </IconButton>
                            </Box>
                            <Typography variant="body1" sx={{ fontWeight: 600 }}>
                              ${(item.productPriceInUSD * item.quantity).toFixed(2)}
                            </Typography>
                            <IconButton 
                              size="small" 
                              onClick={() => removeFromCart(item.productId)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>

              {/* Cart Summary */}
              <Box sx={{ mt: 3, pt: 3, borderTop: `1px solid ${theme.palette.divider}` }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography>Subtotal</Typography>
                  <Typography>${cartTotal.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>Total</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>${cartTotal.toFixed(2)}</Typography>
                </Box>

                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={() => {
                    setCartOpen(false);
                    setCheckoutOpen(true);
                  }}
                  sx={{ 
                    mb: 2,
                    py: 1.5,
                    fontWeight: 600,
                    bgcolor: theme.palette.primary.main,
                    '&:hover': {
                      bgcolor: theme.palette.primary.dark
                    }
                  }}
                >
                  Proceed to Checkout
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => setCartOpen(false)}
                >
                  Continue Shopping
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Drawer>

      {/* Checkout Dialog - EXACT SAME AS SHOP */}
      <Dialog
        open={checkoutOpen}
        onClose={() => !orderPlacing && setCheckoutOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>
          Checkout
        </DialogTitle>
        
        <DialogContent>
          {orderError && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {orderError}
            </Alert>
          )}
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
              Order Summary
            </Typography>
            
            <List dense>
              {cart.map((item) => (
                <ListItem key={item.productId} sx={{ px: 0 }}>
                  <ListItemText 
                    primary={item.productName}
                    secondary={`${item.quantity} × $${item.productPriceInUSD.toFixed(2)}`}
                  />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    ${(item.productPriceInUSD * item.quantity).toFixed(2)}
                  </Typography>
                </ListItem>
              ))}
            </List>
            
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>${cartTotal.toFixed(2)}</Typography>
            </Box>
            
            {/* Delivery Address */}
            <TextField
              fullWidth
              label="Delivery Address"
              value={deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              multiline
              rows={2}
              required
              sx={{ mb: 3 }}
              placeholder="Enter your full delivery address"
            />
            
            {/* Payment Method */}
            <FormControl component="fieldset" fullWidth sx={{ mb: 3 }}>
              <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>
                Payment Method
              </FormLabel>
              <RadioGroup
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                {paymentMethods.map((method) => (
                  <FormControlLabel
                    key={method.id}
                    value={method.id}
                    control={<Radio />}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {method.label}
                        {!method.enabled && (
                          <Chip
                            label="Coming Soon"
                            size="small"
                            sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    }
                    disabled={!method.enabled}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              Only Cash on Delivery is available at the moment. Other payment methods coming soon.
            </Alert>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 0 }}>
          <Button
            onClick={() => setCheckoutOpen(false)}
            disabled={orderPlacing}
          >
            Cancel
          </Button>
          
          <Button
            variant="contained"
            onClick={placeOrder}
            disabled={orderPlacing || !deliveryAddress.trim()}
            sx={{
              bgcolor: theme.palette.primary.main,
              '&:hover': { bgcolor: theme.palette.primary.dark }
            }}
          >
            {orderPlacing ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'white' }} />
                Placing Order...
              </>
            ) : 'Place Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Appointment Booking Dialog */}
      {selectedService && (
        <AppointmentBookingDialog
          open={appointmentDialogOpen}
          onClose={() => setAppointmentDialogOpen(false)}
          orgUniqueId={orgId || ''}
          serviceName={selectedService.productName}
          providerUserName={merchant.officialOrgName}
        />
      )}
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MerchantStore;